<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="page-dashboard">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb" :items="headerItems">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>
            <img
              v-if="defaultLang !== $path('metadata.language', item)"
              class="ml-2"
              :src="`/images/flags/${$path('metadata.language', item)}.svg`"
              width="20"
            />
            <ButtonComponent
              tag="router-link"
              :disabled="!$store.getters['Page/hasRole']('manager')"
              :to="{ name: 'page-edit', params: { id, step: 'publishing' } }"
              :label="labels[item.isPublished ? 'published' : 'unpublished']"
              :icon="{ icon: ['fas', 'circle'], class: item.isPublished ? 'text--success' : 'text--error' }"
              icon-placement="left"
              class="page-dashboard__published"
            />
          </div>
        </template>
        <template #center>
          <transition name="fade">
            <DraftAlert v-if="hasActiveDraft" />
          </transition>
        </template>
      </HeaderComponent>

      <div class="page-dashboard__content">
        <Statistics :addons="addons" data-field="statistics" />
        <Tasks data-field="tasks" :reference="{ onModel: 'Page', ref: id }" :show-create="false" />
        <Deadlines data-field="deadlines" :reference="{ onModel: 'Page', ref: id }" />

        <LastActivity data-field="last-activity" :reference="{ onModel: 'Page', ref: id }" />
      </div>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapi18n } from '@/i18n'
import EditSteps from './Mutate/Steps'
import { mergeDeepRight } from 'ramda'

import Statistics from '@/components/Page/Dashboard/Statistics'
import HeaderComponent from '@/components/Header'
import Deadlines from '@/components/Deadlines/Widget'
import LastActivity from '@/components/LastActivity/Widget'
import Tasks from '@/components/Task/Widget'
import DraftAlert from '@/components/Draft/Alert'

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      draft: true,
    }
  },
  computed: {
    ...mapState('Page', ['item', 'promises']),
    ...mapi18n('labels', {
      pages: ['page', 2],
      edit: 'edit',
      published: 'published',
      unpublished: 'unpublished',
      loading() {
        return this.$t('loading', { resource: this.$tc('page', 1).toLowerCase() }) + '...'
      },
    }),
    ...mapState(['enums']),
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    defaultLang() {
      if (!this.enums) return
      return ((this.enums.Languages || []).find(i => Boolean(i.default)) || {}).value
    },
    breadcrumb() {
      return [
        {
          title: (JSON.parse(sessionStorage.getItem(`page:category`)) || {}).label || this.labels.pages,
          action: { name: 'pages' },
        },
        { title: this.$metaInfo.title },
      ]
    },

    hasActiveDraft() {
      return typeof this.draft === 'string'
    },

    showDraft() {
      return this.$path('item.metadata.draftAvailable') || this.hasActiveDraft
    },

    headerItems() {
      return [
        {
          component: 'DropdownComponent',
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('show'),
                  icon: ['fal', 'ellipsis-v'],
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                props: {
                  tag: 'router-link',
                  label: this.$tc('lead', 2),
                  icon: ['fal', 'users'],
                  to: {
                    name: 'page-leads',
                    params: { reference: this.id },
                    query: this.$route.query,
                  },
                },
              },

              {
                component: 'Preview',
                props: {
                  item: this.item,
                  usePortal: true,
                },
              },

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page:show',
                  query: { page: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },

              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page-form-submit:show',
                  query: { page: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
            ],
          },
        },

        {
          component: 'Actions',
          condition: this.$store.getters.hasAccess('admin'),
          props: {
            placement: 'page',
            query: { page: this.id, 'kvass-draft': this.draft || this.showDraft },
            'use-portal': false,
          },
        },
        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Page/hasRole']('manager'),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.labels.edit,
                  icon: ['fal', 'pen'],
                },
              },
            ],
            dropdown: [
              ...EditSteps(mergeDeepRight(this.item, { ...this.data, userRoles: this.$store.getters })).map(i => {
                return {
                  component: 'ButtonComponent',
                  props: {
                    tag: 'router-link',
                    label: i.label,
                    icon: i.icon,
                    to: {
                      name: 'page-edit',
                      params: { id: this.id, step: i.id },
                      query: this.$route.query,
                    },
                  },
                }
              }),
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page:edit',
                  query: { page: this.id },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page-form:edit',
                  query: { page: this.id },
                },
              },
            ],
          },
        },

        {
          component: 'ButtonComponent',
          condition: this.$store.getters['Page/hasRole']('manager'),
          props: {
            tag: 'router-link',
            label: this.$t('analytics'),
            icon: ['fal', 'analytics'],
            to: {
              name: 'page-analytics',
              params: { id: this.id },
              query: this.$route.query,
            },
          },
        },

        {
          component: 'DropdownComponent',
          condition: this.$store.getters['Page/hasRole'](['manager', 'salesman']),
          props: {
            'close-on-click': false,
          },
          children: {
            default: [
              {
                component: 'ButtonComponent',
                class: '',
                props: {
                  label: this.$t('add'),
                  icon: ['fal', 'plus'],
                  theme: 'primary',
                },
              },
            ],
            dropdown: [
              {
                component: 'ButtonComponent',
                props: {
                  label: this.$tc('lead'),
                  icon: ['fal', 'user'],
                  tag: 'router-link',
                  to: {
                    name: 'page-lead-create',
                    params: { reference: this.id },
                    query: {
                      returnTo: `${this.$route.path}`,
                      ...this.$route.query,
                    },
                  },
                },
              },
              {
                component: 'ButtonComponent',
                condition: this.$store.getters.isAdmin,
                props: {
                  label: this.$tc('integration'),
                  icon: ['fal', 'puzzle-piece'],
                  tag: 'router-link',
                  to: {
                    name: 'page-edit',
                    params: { page: this.id, step: 'integrations' },
                  },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page:add',
                  query: { page: this.id, 'kvass-draft': this.draft || this.showDraft, ...this.$route.query },
                },
              },
              {
                component: 'Actions',
                props: {
                  type: 'list',
                  placement: 'page-form:add',
                  query: { page: this.id, 'kvass-draft': this.draft || this.showDraft },
                },
              },
            ],
          },
        },
      ]
    },
  },
  methods: {
    ...mapActions('Page', {
      fetch(dispatch) {
        return dispatch('fetch', [{ id: this.id }, { query: 'PageDashboard', draft: this.draft }]).then(data => {
          if (!data) return this.$router.push({ name: 'notfound' })
          this.draft = data.draftId
        })
      },
      mutate: 'mutate',
      remove: 'remove',
    }),
  },
  created() {
    this.$bus.$on('task:create', this.fetch)
    this.$bus.$on('task:update', this.fetch)
    this.$bus.$on('task:remove', this.fetch)
  },
  beforeDestroy() {
    this.$bus.$off('task:create', this.fetch)
    this.$bus.$off('task:update', this.fetch)
    this.$bus.$off('task:remove', this.fetch)
  },
  metaInfo() {
    return {
      title: this.item.name || this.labels.loading,
    }
  },
  components: {
    HeaderComponent,
    Deadlines,
    LastActivity,
    Tasks,
    Statistics,
    DraftAlert,
  },
}
</script>

<style lang="scss">
.page-dashboard {
  background-color: $background-grey;

  &__published {
    margin-left: 1rem;
    font-size: 0.65em;
  }

  &__content {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 480px 600px;
    grid-gap: 2rem;

    @include respond-below('phone') {
      grid-template-columns: 1fr;
    }

    & > * {
      animation: fadeIn 300ms ease;
    }
  }

  [data-field='statistics'] {
    grid-column-end: span 3;
  }

  .elder-button__default-tag {
    padding-right: var(--vue-elder-button-padding-x, 1.5em);
  }

  label[for='statistics-selector'] {
    margin-left: auto;
  }

  .tabs {
    &__details {
      border: none !important;
    }

    &__header {
      ul,
      a {
        border: none !important;
      }

      a {
        background-color: rgba(black, 0.025);
      }

      &--active a {
        background-color: White;
      }
    }

    &__tab {
      padding: 0 !important;
    }
  }
}
</style>
